import React from "react";
import LegalShell from "./legal/LegalShell";

const Privacy = () => (
    <LegalShell title="Privacy Policy" effective="20 February 2026">
        <p>
            This Privacy Policy explains how Coffeein7 (<em>[Your registered business name], ABN{" "}
            [XX XXX XXX XXX]</em>) collects, uses, discloses, and protects your personal information.
            We comply with the Australian Privacy Act 1988 and the Australian Privacy Principles (APPs).
        </p>

        <h2 className="font-display font-black text-2xl mt-8">1. What we collect</h2>
        <ul className="list-disc pl-6 space-y-1">
            <li>
                <strong>Customers:</strong> your phone number, first name (optional), order history at
                participating cafés, loyalty stamp count, Beans balance, and any notification consent.
            </li>
            <li>
                <strong>Café Owners:</strong> your Google account email (if you use Google sign-in),
                café name/slug, ABN (if provided), and admin activity logs.
            </li>
            <li>
                <strong>Payments:</strong> we do not store full card numbers. Card data is handled
                directly by <strong>Stripe</strong> (PCI-DSS Level 1 compliant).
            </li>
            <li>
                <strong>Technical data:</strong> IP address, device type, browser, service-worker push
                subscription tokens if you opt in to notifications.
            </li>
        </ul>

        <h2 className="font-display font-black text-2xl mt-8">2. How we use it</h2>
        <ul className="list-disc pl-6 space-y-1">
            <li>To process your orders and apply loyalty rewards.</li>
            <li>To notify you when your order is ready (only if you opt in).</li>
            <li>To provide café owners with anonymised analytics about their customer base.</li>
            <li>To improve the platform and prevent fraud.</li>
        </ul>

        <h2 className="font-display font-black text-2xl mt-8">3. Marketing</h2>
        <p>
            We only send marketing messages when you explicitly opt in — pre-ticked boxes are never used.
            You can unsubscribe at any time by tapping &ldquo;unsubscribe&rdquo; in any message or
            emailing us. This complies with the <em>Spam Act 2003 (Cth)</em>.
        </p>

        <h2 className="font-display font-black text-2xl mt-8">4. Sharing</h2>
        <p>
            Your data is <strong>never sold</strong>. We share it only with:
        </p>
        <ul className="list-disc pl-6 space-y-1">
            <li>
                <strong>The specific café you order from</strong> (your name and last-4 digits of your
                phone, so the barista knows whose coffee is whose).
            </li>
            <li>
                <strong>Service providers</strong> we depend on: Stripe (payments), our hosting provider,
                email/SMS providers if enabled.
            </li>
            <li>
                <strong>Law enforcement</strong> when compelled by a valid warrant or court order.
            </li>
        </ul>

        <h2 className="font-display font-black text-2xl mt-8">5. Cross-café identity</h2>
        <p>
            When you use your phone number at a second Coffeein7 café, we display your first name to
            that café&rsquo;s owner as an &ldquo;Is this you? Sam&rdquo; verification prompt. No spend
            data, order history, or other personal details are shared cross-café without your consent.
        </p>

        <h2 className="font-display font-black text-2xl mt-8">6. Where your data is stored</h2>
        <p>
            Data is stored on servers hosted within the Asia-Pacific region. Coffeein7 uses industry-standard
            encryption in transit (TLS 1.2+) and at rest.
        </p>

        <h2 className="font-display font-black text-2xl mt-8">7. Your rights</h2>
        <p>
            You can request a copy of your data or ask us to delete it by emailing{" "}
            <a href="mailto:admin@coffeein7.com" className="text-[#FF4F18] hover:underline">
                admin@coffeein7.com
            </a>
            . We respond within 30 days. You can also complain to the Office of the Australian Information
            Commissioner (OAIC) at{" "}
            <a href="https://www.oaic.gov.au/" target="_blank" rel="noreferrer" className="text-[#FF4F18] hover:underline">
                oaic.gov.au
            </a>
            .
        </p>

        <h2 className="font-display font-black text-2xl mt-8">8. Data retention</h2>
        <p>
            Order and loyalty data is retained while your account is active plus 24 months of inactivity,
            after which anonymised aggregates are kept and personal identifiers are deleted.
        </p>

        <h2 className="font-display font-black text-2xl mt-8">9. Cookies</h2>
        <p>
            We use minimal cookies and browser storage strictly for authentication, cart state, and
            saving your phone number locally so you don&rsquo;t have to re-enter it. We do not use
            third-party ad-tracking cookies.
        </p>

        <p className="text-xs text-[#8C7A72] pt-6">
            This document is a plain-language draft. Please have a privacy lawyer review it before public
            launch, and update the ABN and business details in section 1 above.
        </p>
    </LegalShell>
);

export default Privacy;
